import React, { useState, useEffect } from 'react';
import { Plus, Check, AlertCircle } from 'lucide-react';
import { buildApiUrl } from '../../utils/apiSetup';
import { useNotification } from '../../contexts/NotificationContext';

const PensionDAConfig = () => {
  const [configs, setConfigs] = useState([]);
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Form states
  const [daPercentage, setDaPercentage] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('/api/v1/pension-payroll/da-config'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setConfigs(data);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch DA configurations.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const response = await fetch(buildApiUrl('/api/v1/pension-payroll/da-config'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          da_percentage: parseFloat(daPercentage),
          effective_from: effectiveFrom,
          remarks: remarks,
          is_active: 1
        })
      });

      if (response.ok) {
        setSuccess('DA Configuration created successfully.');
        setDaPercentage('');
        setEffectiveFrom('');
        setRemarks('');
        setShowAddModal(false);
        fetchConfigs();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create configuration.');
      }
    } catch (err) {
      setError('Network error occurred.');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Dearness Allowance (DA) Configuration</h2>
          <p className="text-sm text-gray-500 mt-1">Manage dearness allowance rates for pensioners</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium text-sm"
        >
          <Plus className="w-4 h-4" /> Add Rate
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading configurations...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 text-gray-700 text-xs font-semibold uppercase tracking-wider">
              <tr>
                <th className="px-6 py-3 text-left">Effective From</th>
                <th className="px-6 py-3 text-left">DA Percentage</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Remarks</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm text-gray-700">
              {configs.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{c.effective_from}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{c.da_percentage}%</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      c.is_active === 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {c.is_active === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">{c.remarks || '-'}</td>
                </tr>
              ))}
              {configs.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">No DA configurations found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add DA Configuration</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">DA Percentage (%)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={daPercentage}
                  onChange={(e) => setDaPercentage(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effective From</label>
                <input
                  type="date"
                  required
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                ></textarea>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium"
                >
                  Save Configuration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PensionDAConfig;
